import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import useAdminUploadImage from "../hooks/useAdminUploadImage";
import Medal from "../interfaces/medal";
import useAdminCreateMedal from "../hooks/useAdminCreateMedal";

const medalSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters"),
  description: z.string().min(10, "Description is too short"),
  amountBronze: z.coerce.number().min(1, "Must be greater than 0"),
  amountSilver: z.coerce.number().min(1, "Must be greater than 0"),
  amountGold: z.coerce.number().min(1, "Must be greater than 0"),
  amountPlatinum: z.coerce.number().min(1, "Must be greater than 0"),
  medalType: z.object({
    category: z.enum(["1", "2", "3", "4"])
  }),
  image: z
    .any()
    .refine((files) => files?.length > 0, "Image is required")
    .refine((files) => files?.[0]?.size <= 5000000, "Max file size is 5MB")
});

type MedalFormValues = z.infer<typeof medalSchema>;

const AdminCreateMedalForm = () => {
  const token = localStorage.getItem("token") || "";
  const { mutate: uploadImage, isPending: isUploadingImage } = useAdminUploadImage(token);
  const { mutate: createMedal, isPending: isCreatingMedal, isSuccess: isCreateMedalSuccess, isError: isCreateMedalError } = useAdminCreateMedal(token);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MedalFormValues>({
    resolver: zodResolver(medalSchema),
    defaultValues: {
      medalType: {
        category: "1"
      }
    },
  });

  const onSubmit = (data: MedalFormValues) => {
    const formData = new FormData();

    const newFileName = `${Date.now()}_${data.name.replace(/\s+/g, "_")}.${data.image[0].name.split('.').pop()}`;
    const newFile = new File([data.image[0]], newFileName, { type: data.image[0].type });
    formData.append("file", newFile);
    uploadImage(formData, {
      onSuccess: (response) => {
        console.log("Image uploaded successfully:", response);
        const medal = {
          name: data.name,
          description: data.description,
          amountForBronze: data.amountBronze,
          amountForSilver: data.amountSilver,
          amountForGold: data.amountGold,
          amountForPlatinum: data.amountPlatinum,
          pathImage: response,
          medalType: {
            id: parseInt(data.medalType.category)
          }
        } as Medal;
        createMedal(medal);
      }
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '400px' }}>
      
      <input {...register("name")} placeholder="Medal Name" />
      {errors.name && <span style={{ color: "red" }}>{errors.name.message}</span>}

      <input {...register("description")} placeholder="Medal Description" />
      {errors.description && <span style={{ color: "red" }}>{errors.description.message}</span>}

      <input type="number" {...register("amountBronze")} placeholder="Bronze Amount" />
      {errors.amountBronze && <span style={{ color: "red" }}>{errors.amountBronze.message}</span>}
      <input type="number" {...register("amountSilver")} placeholder="Silver Amount" />
      {errors.amountSilver && <span style={{ color: "red" }}>{errors.amountSilver.message}</span>}

      <input type="number" {...register("amountGold")} placeholder="Gold Amount" />
      {errors.amountGold && <span style={{ color: "red" }}>{errors.amountGold.message}</span>}
      <input type="number" {...register("amountPlatinum")} placeholder="Platinum Amount" />
      {errors.amountPlatinum && <span style={{ color: "red" }}>{errors.amountPlatinum.message}</span>}

      <select {...register("medalType.category")}>
        <option value="1">Collectionables</option>
        <option value="2">Pokedex</option>
        <option value="3">Medals</option>
        <option value="4">Type Captures</option>
      </select>

      <label htmlFor="medal-image">Medal Image:</label>
      <input 
        id="medal-image" 
        type="file" 
        accept="image/*" 
        {...register("image")} 
      />
      {errors.image && <span style={{ color: "red" }}>{errors.image.message as string}</span>}

      <button
        type="submit"
        disabled={isUploadingImage || isCreatingMedal}
        style={{
          padding: "10px",
          backgroundColor: (isUploadingImage || isCreatingMedal) ? "#ccc" : "#007bff",
          color: "white",
          cursor: "pointer",
        }}
      >
        {isUploadingImage || isCreatingMedal ? "Processing..." : "Upload Medal"}
      </button>

      {isCreateMedalSuccess && <p style={{ color: "green" }}>Success!</p>}
      {isCreateMedalError && <p style={{ color: "red" }}>Error creating medal.</p>}
    </form>
  );
};

export default AdminCreateMedalForm;